import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface BreadcrumbItem {
  label: string;
  url: string;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly dynamicLabels = new Map<string, string>();
  private customBreadcrumbs: BreadcrumbItem[] | null = null;
  private readonly subject = new BehaviorSubject<BreadcrumbItem[]>([]);

  readonly breadcrumbs$: Observable<BreadcrumbItem[]> = this.subject.asObservable();

  readonly parentUrl$: Observable<string | null> = this.breadcrumbs$.pipe(
    map(crumbs => (crumbs.length >= 2 ? crumbs[crumbs.length - 2].url : null))
  );

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.dynamicLabels.clear();
        this.customBreadcrumbs = null;
        this.rebuild();
      });
  }

  setCustomBreadcrumbs(crumbs: BreadcrumbItem[]): void {
    this.customBreadcrumbs = crumbs;
    this.subject.next(crumbs);
  }

  setDynamicCrumb(paramKey: string, label: string): void {
    this.dynamicLabels.set(paramKey, label);
    this.rebuild();
  }

  back(): void {
    const crumbs = this.subject.getValue();
    if (crumbs.length >= 2) {
      void this.router.navigate([crumbs[crumbs.length - 2].url]);
    }
  }

  private rebuild(): void {
    if (this.customBreadcrumbs) {
      this.subject.next(this.customBreadcrumbs);
      return;
    }

    const crumbs = this.buildBreadcrumbs(this.router.routerState.snapshot.root);
    this.subject.next(crumbs);
  }

  private buildBreadcrumbs(
    snapshot: ActivatedRouteSnapshot,
    url = '',
    crumbs: BreadcrumbItem[] = []
  ): BreadcrumbItem[] {
    const primary = snapshot.children.find(c => c.outlet === PRIMARY_OUTLET);
    if (!primary) return crumbs;

    const segmentPath = primary.url.map(s => s.path).join('/');
    const nextUrl = segmentPath ? `${url}/${segmentPath}` : url;

    const rawBreadcrumb: string | undefined = primary.data['breadcrumb'];

    // Only add a crumb for routes that consume actual URL segments
    if (rawBreadcrumb !== undefined && segmentPath !== '') {
      let label = rawBreadcrumb;

      if (rawBreadcrumb === '') {
        // Dynamic segment — extract param name from the route config path (e.g. ':person-id' → 'person-id')
        const routePath = primary.routeConfig?.path ?? '';
        const paramKey = routePath.startsWith(':')
          ? routePath.slice(1)
          : Object.keys(primary.params)[0];
        if (paramKey) {
          label = this.dynamicLabels.get(paramKey) ?? '';
        }
      }

      if (label !== '') {
        crumbs.push({ label, url: nextUrl });
      }
    }

    return this.buildBreadcrumbs(primary, nextUrl, crumbs);
  }
}
